import isNil from "lodash/isNil";
import { DetailsPanelData } from "PFApp/booking/components/details_panel";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { LoadingDots } from "PFComponents/loading_dots";
import { Markdown } from "PFComponents/markdown";
import { useProfilesWarnings } from "PFCore/hooks/queries/profile";
import { useProfile } from "PFCore/hooks/queries/profile/use_profile";
import { CalendarRange } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useBookingModulePermissions from "../../../../../use_booking_module_permissions";
import css from "../../../../parts/overview/detail_view/detail_view.module.scss";
import { Label } from "../parts/label";
import { Buttons } from "./buttons";
import { Details } from "./details";
import { Skills } from "./skills";

export type ProfileDetailContentProps = {
  data: DetailsPanelData;
  bookingModuleCalendarRange?: CalendarRange;
};

export const ProfileDetailContent = ({ data, bookingModuleCalendarRange }: ProfileDetailContentProps) => {
  const { t } = useTranslation("translation");
  const { data: profile, isLoading: isProfileLoading } = useProfile(data?.id);
  const { warningsByProfile, isLoading: isWarningsLoading } = useProfilesWarnings({
    profiles: [{ id: profile?.id || -1 }],
    calendarRange: bookingModuleCalendarRange,
    enabled: !!profile
  });
  const { canProfileCreateBooking } = useBookingModulePermissions();
  const canSeeBookingButton = canProfileCreateBooking(profile?.id);

  const utilization = useMemo(() => {
    if (isNil(profile?.id) || isNil(warningsByProfile[profile!.id])) {
      return 0;
    }
    return Math.round((warningsByProfile[profile!.id].billableUtilization || 0) * 100);
  }, [profile, warningsByProfile]);

  if (!profile || isProfileLoading || isWarningsLoading) {
    return <LoadingDots />;
  }

  const bookingsTotal = profile.availability?.bookings?.length || 0;

  const { summary } = profile;
  const { onFilterByProfile } = data.bookingModuleDetails ?? {};
  const handleBookingsClick = onFilterByProfile
    ? () => onFilterByProfile({ profileId: profile.id })
    : undefined;

  return (
    <Stack className={css.root} gap="spacingLg">
      <Divider />
      <Details
        onBookingsClick={handleBookingsClick}
        bookingsTotal={bookingsTotal}
        utilization={utilization}
      />
      <Divider />
      {canSeeBookingButton && (
        <>
          <Buttons id={profile.id} />
          <Divider />
        </>
      )}
      <div className={css.item}>
        <Label text={t("bio")} />
        {summary ? <Markdown raw={summary} emojify={false} /> : "-"}
      </div>
      <Divider />
      <Skills profile={profile} />
    </Stack>
  );
};
